import { useAppSelector, useAppStore } from 'hooks/redux';
import React, { startTransition, useEffect, useRef, useState } from 'react';
import type { RouterProviderProps } from 'react-router';
import {
  type DataRouter,
  type RouteObject,
  RouterProvider,
  type ShouldRevalidateFunctionArgs,
  createBrowserRouter,
  redirect,
  useRouteError,
} from 'react-router-dom';

import { accountsActions, accountsSelectors } from 'app-components/flux/accounts';
import { authSelectors } from 'app-components/flux/auth';
import {
  billableInvoiceItemsActions,
  billableInvoiceItemsSelectors,
} from 'app-components/flux/billable-invoice-items/billableInvoiceItemsSlice';
import { categoriesActions, categoriesSelectors } from 'app-components/flux/categories';
import { customersActions, customersSelectors } from 'app-components/flux/customers';
import { termsActions, termsSelectors } from 'app-components/flux/terms';
import { datasetsUtils } from 'app-components/flux/datasets';
import { featureFlagsSelectors, type featureFlagsTypes } from 'app-components/flux/feature-flags';
import type { InvoiceData } from 'app-components/flux/invoice-actions';
import { viewInvoiceData } from 'app-components/flux/invoice-actions';
import { invoiceItemsActions, invoiceItemsSelectors } from 'app-components/flux/invoice-items/invoiceItemsSlice';
import { preferencesActions } from 'app-components/flux/preferences';
import { projectsActions, projectsSelectors } from 'app-components/flux/projects';
import { receiptsSelectors } from 'app-components/flux/receipts';
import { salesTaxesActions, salesTaxesSelectors } from 'app-components/flux/sales-taxes/salesTaxesSlice';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
} from 'app-components/flux/scheduled-transactions';
import { tagsActions, tagsSelectors } from 'app-components/flux/tags';
import { taxFormsActions, taxFormsSelectors } from 'app-components/flux/tax-forms';
import { taxLineItemsActions, taxLineItemsSelectors } from 'app-components/flux/tax-line-items';
import * as taxesActions from 'app-components/flux/taxes/taxesActions';
import * as taxesSelectors from 'app-components/flux/taxes/taxesSelectors';
import { transactionsActions, transactionsSelectors } from 'app-components/flux/transactions';
import { fetchOnce } from 'app-components/hooks/utils';

import { loadTransactions } from 'containers/Transactions/loadTransactions';

import FallbackComponent from 'components/ErrorBoundary/FallbackComponent';
import LoadingView from 'components/LoadingView';
import Login from 'containers/App/Login';
import Logout from 'containers/App/Logout';

import { businessActions, businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { mileageTrackerPurposeActions } from 'app-components/flux/mileage-tracker-purpose/mileageTrackerPurposeSlice';
import { mileageTrackerRatesActions } from 'app-components/flux/mileage-tracker-rates/mileageTrackerRatesSlice';
import { mileageTrackerVehicleActions } from 'app-components/flux/mileage-tracker-vehicle/mileageTrackerVehicleSlice';
import { mileageTrackerActions } from 'app-components/flux/mileage-tracker/mileageTrackerSlice';
import { reportConfigurationsActions, reportConfigurationsSelectors } from 'app-components/flux/report-configurations';
import {
  transactionRulesActions,
  transactionRulesSelectors,
} from 'app-components/flux/transaction-rules/transactionRulesSlice';
import {
  mcpAuthorizePath,
  mcpLoginPath,
  mileageTrackerPath,
  receiptCenterPath,
  taxPlannerPath,
} from 'containers/App/paths';
import { loadReceiptCenter } from './receiptCenterRoute';
import { EntitledGate, EntitledRoutesContext, EntitledRoutesFallback, SignedInGate } from './routeGates';
import { loadTransactionReview, transactionReviewPath } from './transactionReviewRoute';
import ErrorPage from 'containers/ErrorPage';
import LayoutAuth from 'containers/LayoutAuth';
import LayoutLoggedOut from 'containers/LayoutLoggedOut';
import NotFoundPage from 'containers/NotFoundPage';
import type { RootState, RootStore } from 'store';
import {
  isChunkLoadError,
  mayReloadForChunkLoadError,
  reloadForChunkLoadError,
  retryChunkLoad,
} from 'utils/chunkLoadError';
import { bugsnagNotify, bugsnagNotifyChunkLoadReload } from 'utils/core/bugsnag';
import { loadReportRoute } from './reportRouteLoader';

// The router is created once per page and Fast Refresh would keep it, so an edit that reaches this module reloads.
/////////////////////////////////////////////
import.meta.hot?.accept(() => window.location.reload());
/////////

// A reload the user stops leaves the page up, so the spinner gives way to the error page after this long.
const reloadStallMs = 15_000;

const reloadsFor = (error: unknown): boolean => isChunkLoadError(error) && mayReloadForChunkLoadError();

const ErrorBoundaryRouter = () => {
  const error = useRouteError() as Error;
  // React Router keeps a route's failed import until the page reloads, so a chunk that failed to load reloads the page
  // instead of showing the error page. Decided per error, since React Router reuses this boundary for the next route
  // error, and kept in state so a reload that can't start or stalls shows the error page.
  const [decision, setDecision] = useState(() => ({ error, reloading: reloadsFor(error) }));
  if (decision.error !== error) setDecision({ error, reloading: reloadsFor(error) });
  const { reloading } = decision;
  const reloadStartedFor = useRef<unknown>(undefined);

  useEffect(() => {
    if (!error) return undefined;
    if (!reloading) {
      // A reload that stalled was reported when it started.
      if (reloadStartedFor.current !== error) bugsnagNotify(error);
      return undefined;
    }
    if (reloadStartedFor.current !== error) {
      if (!reloadForChunkLoadError((onReported) => bugsnagNotifyChunkLoadReload(error, onReported))) {
        setDecision({ error, reloading: false });
        return undefined;
      }
      reloadStartedFor.current = error;
    }
    const stalled = setTimeout(() => setDecision({ error, reloading: false }), reloadStallMs);
    return () => clearTimeout(stalled);
  }, [error, reloading]);

  return reloading ? <LoadingView style={{ height: '100vh' }} /> : <FallbackComponent error={error} />;
};

// React Router keeps a route's failed import, so a chunk that fails to load is retried inside the route's lazy().
const retryRouteChunks = (routes: RouteObject[]): RouteObject[] => {
  for (const route of routes) {
    const { lazy } = route;
    if (typeof lazy === 'function') route.lazy = () => retryChunkLoad(lazy);
    if (route.children) retryRouteChunks(route.children);
  }
  return routes;
};

const ENTITLED_ROUTE_ID = 'entitled';

const selectShowsEntitledRoutes = (state: RootState): boolean => {
  if (!authSelectors.isAuthTokenValid(state)) return false;
  const dataset = authSelectors.getCurrentDataset(state);
  return Boolean(dataset && datasetsUtils.hasActiveEntitlement(dataset));
};

const isFlagOn = (state: RootState, key: featureFlagsTypes.FeatureFlagsKeys): boolean =>
  Boolean(featureFlagsSelectors.getFeatureFlag(state, key));

// Loaders redirect to /oops when one of these is off. Routes that opt out of revalidation still re-check their gate.
const loaderFlagKeys = [
  'spendingPlan',
  'upcoming',
  'spendingWatchList',
  'netWorthV2Feature',
  'spendingDashboard',
  'reports',
  'goals',
  'achievements',
  'wellnessPageRelease',
] as const satisfies readonly featureFlagsTypes.FeatureFlagsKeys[];

const selectLoaderGates = (state: RootState): string =>
  [
    ...loaderFlagKeys.map((key) => isFlagOn(state, key)),
    entitlementsSelectors.getBusinessEnabled(state),
    receiptsSelectors.selectReceiptCenterEnabled(state),
  ].join();

const isInvestingOn = (state: RootState): boolean => isFlagOn(state, 'investmentsAcme');
const isPlanningToolsOn = (state: RootState): boolean =>
  isFlagOn(state, 'planningTools') && isFlagOn(state, 'reportsMoveRfndtrkrCrdtscr');

// While its flag is off, a flagged route shows the not-found page, as an unmatched path does.
const flagGatedPage = (isOn: (state: RootState) => boolean, Page: React.ComponentType): React.ComponentType => {
  const FlagGatedPage = () => (useAppSelector(isOn) ? <Page /> : <NotFoundPage />);
  return FlagGatedPage;
};

// The main app's catch-all, and the entitled route's only child until the main app's routes are added.
const notFoundRoute: RouteObject = { path: '*', element: <NotFoundPage /> };

// The main app. These routes are added the first time the user is signed in with an entitled dataset, so until then
// the sign-in and no-subscription pages load none of their chunks or loaders. Loaders read flags when they run.
const entitledRoutes = (store: RootStore): RouteObject[] => {
  const { dispatch } = store;
  const flagOn = (key: featureFlagsTypes.FeatureFlagsKeys) => isFlagOn(store.getState(), key);
  const businessEnabled = () => entitlementsSelectors.getBusinessEnabled(store.getState());
  const receiptCenterEnabled = () => receiptsSelectors.selectReceiptCenterEnabled(store.getState());

  return [
    {
      path: '/',
      lazy: () => import('containers/OverviewPage'),
      loader: () => {
        dispatch(preferencesActions.getPreferences({ section: 'shared', group: 'dataset' }));
        return null;
      },
      shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
        return currentUrl.pathname === nextUrl.pathname;
      },
    },
    {
      path: transactionReviewPath,
      loader: loadTransactionReview,
    },
    {
      path: '/transactions',
      lazy: () => import('containers/Transactions'),
      loader: loadTransactions,
      shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
        // Always revalidate if path changed
        if (currentUrl.pathname !== nextUrl.pathname) {
          return true;
        }

        // Otherwise, only revalidate if displayNode query param changed
        const currentDisplayNode = currentUrl.searchParams.get('displayNode');
        const nextDisplayNode = nextUrl.searchParams.get('displayNode');

        return currentDisplayNode !== nextDisplayNode;
      },
    },
    {
      path: '/spending-plan/:tabs?/:selectedPlannedId?',
      lazy: () => import('containers/SpendingPlan'),
      loader: () => {
        if (!flagOn('spendingPlan')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        return null;
      },
    },
    {
      path: '/upcoming/:tabName?',
      lazy: () => import('containers/Upcoming'),
      loader: () => {
        if (!flagOn('upcoming')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        return null;
      },
    },
    {
      path: '/planning-tools/:tabName?',
      lazy: async () => {
        const { Component } = await import('containers/PlanningTools');
        return { Component: flagGatedPage(isPlanningToolsOn, Component) };
      },
    },
    {
      path: '/watchlist/:selectedWatchlistId?',
      lazy: () => import('containers/SpendingWatchlist'),
      loader: () => {
        if (!flagOn('spendingWatchList')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        return null;
      },
    },
    {
      path: '/net-worth',
      lazy: () => {
        fetchOnce(reportConfigurationsActions.getReportConfigurations, reportConfigurationsSelectors);
        return import('containers/NetWorth');
      },
      loader: () => {
        if (!flagOn('netWorthV2Feature')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        return null;
      },
    },
    {
      path: '/spending',
      lazy: () => {
        fetchOnce(reportConfigurationsActions.getReportConfigurations, reportConfigurationsSelectors);
        fetchOnce(accountsActions.getAccounts, accountsSelectors);
        fetchOnce(categoriesActions.getCategories, categoriesSelectors);
        fetchOnce(tagsActions.getTags, tagsSelectors);
        fetchOnce(transactionsActions.getTransactions, transactionsSelectors);
        return import('containers/Spending');
      },
      loader: () => {
        if (!flagOn('spendingDashboard')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        return null;
      },
    },
    {
      path: '/reports/:tabName?/:groupBy?/:layoutView?',
      lazy: () => {
        fetchOnce(taxFormsActions.getTaxForms, taxFormsSelectors);
        fetchOnce(taxLineItemsActions.getTaxLineItems, taxLineItemsSelectors);
        fetchOnce(businessActions.getBusiness, businessSelectors);
        fetchOnce(reportConfigurationsActions.getReportConfigurations, reportConfigurationsSelectors);
        return import('containers/OpenSaveReports');
      },
      loader: ({ request }: { request: Request }) => loadReportRoute({ request, reports: flagOn('reports'), dispatch }),
    },
    {
      path: '/investing/:selectedInvestmentId?/:newsfeedArticleId?',
      lazy: async () => {
        const { Component } = await import('containers/Investments/investmentsIndex');
        return { Component: flagGatedPage(isInvestingOn, Component) };
      },
    },
    {
      path: '/goals/:goalId?',
      lazy: () => import('containers/Goals'),
      loader: async ({ params }: { params: { goalId?: string | null } }) => {
        if (!flagOn('goals')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        if (params?.goalId) {
          const { goalId } = params;
          if (goalId === 'by-account') {
            // we've since moved this to appRefreshAll saga, keeping here in case-we want to re-introduce
            dispatch(transactionsActions.getGoalTransactions());
          } else if (/^\d+$/.test(goalId) && goalId.length === 18) {
            // verify GUID
            // specific goal view, grab goal-specific contributions
            dispatch(transactionsActions.getGoalTransactionsForGoal(goalId));
          }
        }
        return null;
      },
    },
    {
      path: '/settings/:settingSelected?/:itemSelected?',
      lazy: () => {
        fetchOnce(taxFormsActions.getTaxForms, taxFormsSelectors);
        fetchOnce(taxLineItemsActions.getTaxLineItems, taxLineItemsSelectors);
        fetchOnce(transactionRulesActions.fetch, transactionRulesSelectors);
        fetchOnce(categoriesActions.getCategories, categoriesSelectors);
        return import('containers/Settings');
      },
      loader: () => {
        return null;
      },
    },
    {
      path: '/get-started',
      lazy: () => import('containers/SetupPageAcme/flows/naturalFlowFunc'),
    },
    {
      path: '/financial-wellness',
      lazy: () => {
        fetchOnce(transactionsActions.getTransactions, transactionsSelectors);
        return import('containers/WellnessScore');
      },
      loader: () => {
        if (!flagOn('wellnessPageRelease')) {
          return redirect('/oops');
        }
        return null;
      },
    },
    {
      path: '/achievements',
      lazy: () => import('containers/Achievements'),
      loader: () => {
        if (!flagOn('achievements')) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        return null;
      },
    },
    {
      lazy: () => import('containers/Customers'),
      path: '/clients',
      loader: () => {
        if (!businessEnabled()) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        fetchOnce(customersActions.getCustomers, customersSelectors);
        fetchOnce(projectsActions.getProject, projectsSelectors);
        dispatch(billableInvoiceItemsActions.fetch());
        dispatch(invoiceItemsActions.fetch());
        return null;
      },
      shouldRevalidate: () => !businessEnabled(),
      children: [
        {
          index: true,
          lazy: () => import('containers/Customers/components/CustomerDashboardContainer'),
        },
        {
          path: ':customerId',
          lazy: () => import('containers/Customers/components/CustomerDetailsPage'),
        },
        {
          path: ':customerId/:projectId',
          lazy: () => import('containers/Projects/components/ProjectDetailsView'),
        },
      ],
    },
    {
      path: '/sales-and-billing',
      lazy: () => import('containers/SalesAndBilling'),

      loader: () => {
        if (!businessEnabled()) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        fetchOnce(customersActions.getCustomers, customersSelectors);
        fetchOnce(projectsActions.getProject, projectsSelectors);
        fetchOnce(invoiceItemsActions.fetch, invoiceItemsSelectors);
        fetchOnce(billableInvoiceItemsActions.fetch, billableInvoiceItemsSelectors);
        fetchOnce(salesTaxesActions.fetch, salesTaxesSelectors);
        fetchOnce(termsActions.getTerms, termsSelectors);
        return null;
      },
      shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
        return !businessEnabled() || currentUrl.pathname !== nextUrl.pathname;
      },
      children: [
        { index: true, loader: () => redirect('/sales-and-billing/invoices') },
        {
          path: ':tab',
          lazy: () => import('containers/SalesAndBilling'),
        },
      ],
    },
    {
      path: '/sales-and-billing/products-and-services',
      lazy: () => import('containers/ProductsAndServices'),
      loader: () => {
        if (!businessEnabled()) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        fetchOnce(invoiceItemsActions.fetch, invoiceItemsSelectors);
        fetchOnce(categoriesActions.getCategories, categoriesSelectors);
        return null;
      },
    },
    {
      path: taxPlannerPath,
      lazy: () => import('containers/TaxPlanner'),
      loader: () => {
        fetchOnce(scheduledTransactionsActions.getScheduledTransactions, scheduledTransactionsSelectors);
        fetchOnce(
          taxesActions.getTaxInputs, // TODO: replace with getTaxCalculations once ready
          taxesSelectors,
        );
        return null;
      },
    },
    {
      path: mileageTrackerPath,
      lazy: () => import('containers/MileageTracker'),
      loader: () => {
        if (!businessEnabled()) {
          return redirect('/oops'); // TODO: refactor /oops redirects - redirecting causing context loss if FF is flipped back + /oops is different from 404 page
        }
        fetchOnce(businessActions.getBusiness, businessSelectors);
        dispatch(mileageTrackerActions.fetch());
        dispatch(mileageTrackerPurposeActions.fetch());
        dispatch(mileageTrackerVehicleActions.fetch());
        dispatch(mileageTrackerRatesActions.getRates());
        return null;
      },
      shouldRevalidate: ({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs) => {
        // Always revalidate if path changed or business access is gone
        if (!businessEnabled() || currentUrl.pathname !== nextUrl.pathname) {
          return true;
        }
        return false;
      },
    },
    {
      path: receiptCenterPath,
      lazy: () => import('containers/ReceiptCenter'),
      loader: () => loadReceiptCenter(receiptCenterEnabled(), dispatch),
      shouldRevalidate: () => !receiptCenterEnabled(),
    },
    {
      path: '/oops',
      element: <NotFoundPage />,
    },
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    {
      path: '/docs/:itemId?',
      lazy: () => import('containers/SharedComponentsPage'),
    },
/////////////
    notFoundRoute,
  ];
};

const appRoutes = (store: RootStore, includeEntitledRoutes: boolean): RouteObject[] => [
  {
    element: <LayoutLoggedOut />,
    ErrorBoundary: ErrorBoundaryRouter,
    children: [
      // globally available routes regardless of login or entitlement state ----------------------------------------
      {
        path: '/acknowledgements',
        lazy: () => import('containers/Legal/Acknowledgements'),
      },
      {
        path: '/web-acknowledgements.txt',
        lazy: () => import('containers/WebAcknowledgements'),
      },
      {
        path: '/error',
        element: <ErrorPage />,
      },
      {
        path: '/logout',
        element: <Logout />,
      },
      {
        path: '/login',
        element: <Login />,
      },
      {
        path: '/view-invoice',
        lazy: () => import('containers/ViewInvoice'),
        loader: async ({ request }: { request: Request }): Promise<{ invoiceData: InvoiceData } | { error: true }> => {
          const JWT = new URL(request.url).searchParams.get('token');
          let respData: { invoiceData: InvoiceData; JWT: string | null } | { error: true } | undefined;

          try {
            if (!JWT) {
              throw 'no invoice id param';
            }

            const invoiceData = await viewInvoiceData(JWT);

            // The service answers a bad token with { errors: [...] } in place of the invoice.
            if ('errors' in invoiceData) {
              throw (invoiceData as { errors?: unknown[] }).errors?.[0] ?? 'Unknown error at view invoice';
            }

            respData = { invoiceData, JWT };
          } catch (err) {
            assert(false, err);
            respData = { error: true };
          }
          return respData;
        },
      },
      {
        path: '/automation',
        lazy: () => import('containers/Automation'),
      },
      {
        path: '/pick-your-plan',
        lazy: () => import('containers/PickYourPlanPage'),
      },
      {
        path: '/share-accept',
        lazy: () => import('containers/App/useShareAccept'),
      },
      {
        path: '/stripe-redirect',
        lazy: () => import('components/Dialogs/AccountDiscovery/WidgetAuthenticator/StripeOAuth/StripeOAuthRedirect'),
      },
      {
        path: '/partner-auth',
        lazy: () => import('containers/PartnerAuth'),
      },
      {
        path: mcpAuthorizePath,
        lazy: () => import('containers/McpAuthorize'),
      },
      {
        path: mcpLoginPath,
        lazy: () => import('containers/McpLogin'),
      },
      {
        path: '/finicity',
        lazy: () => import('containers/Finicity'),
      },
      {
        element: <LayoutAuth />,
        children: [
///////////////////////////////////////////////////
          // Under LayoutAuth so signed-out users sign in in place and return to the same URL.
          {
            path: '/dev-sso',
            lazy: () => import('containers/DevSsoSender'),
          },
///////////////////
          {
            // Signed out: the sign-in home on every path. Signed in: the dataset layout.
            element: <SignedInGate />,
            children: [
              {
                // Entitled dataset: the main app. Otherwise the home, No Subscription or the auth dialog.
                id: ENTITLED_ROUTE_ID,
                element: <EntitledGate />,
                HydrateFallback: EntitledRoutesFallback,
                children: includeEntitledRoutes ? entitledRoutes(store) : [notFoundRoute],
              },
            ],
          },
        ],
      },
    ],
  },
];

/**
 * Adds the main app's routes the first time the user is signed in with an entitled dataset, and reruns the current
 * route's loaders when a value they redirect on changes. Returns whether EntitledGate can render the main app.
 */
const useEntitledRoutes = (router: DataRouter, store: RootStore, includedAtStart: boolean): boolean => {
  const showsEntitledRoutes = useAppSelector(selectShowsEntitledRoutes);
  const [patched, setPatched] = useState(includedAtStart);
  const active = showsEntitledRoutes && patched;

  useEffect(() => {
    if (!showsEntitledRoutes || patched) return;
    router.patchRoutes(ENTITLED_ROUTE_ID, retryRouteChunks(entitledRoutes(store)));
    // In a transition, like the router's own updates, so the main app never renders against a stale location.
    startTransition(() => setPatched(true));
  }, [router, store, showsEntitledRoutes, patched]);

  // Revalidating also loads the routes just added, and re-checks gates that changed while the main app was closed.
  const revalidateKey = useAppSelector((state) => (active ? selectLoaderGates(state) : null));
  const revalidatedKey = useRef(revalidateKey);
  useEffect(() => {
    if (revalidateKey === revalidatedKey.current) return;
    revalidatedKey.current = revalidateKey;
    if (revalidateKey !== null) void router.revalidate();
  }, [router, revalidateKey]);

  return active;
};

const AppRouter = (props: Omit<RouterProviderProps, 'router'>) => {
  const store = useAppStore();
  // One router per page: a new router renders its lazy routes as empty until they load, which remounted the app.
  const [includedAtStart] = useState(() => selectShowsEntitledRoutes(store.getState()));
  const [router] = useState(() => createBrowserRouter(retryRouteChunks(appRoutes(store, includedAtStart))));
  const entitledRoutesActive = useEntitledRoutes(router, store, includedAtStart);

  return (
    <EntitledRoutesContext.Provider value={entitledRoutesActive}>
      <RouterProvider {...props} router={router} />
    </EntitledRoutesContext.Provider>
  );
};

export default React.memo(AppRouter);
